import { z } from 'zod';
import { EmailSchema, HistoryEntrySchema, IDSchema, IsoTimestampSchema, MoneyCentsSchema, SchemaVersionSchema, } from './shared.js';
/**
 * SO-001 monthly commission statement — ONE contractor, ONE calendar month.
 *
 * This is the per-rep monthly payout record the SO-001 plan actually requires
 * (replacing the legacy per-invoice flat-% `commission` rows). It stores the
 * computed breakdown from the monthly engine (`@rr-crm/shared/commission`
 * `computeMonthlyRepCommission`) plus the arrears lifecycle.
 *
 * # Arrears lifecycle (no clawback by design — SO-001 §6.4 intent)
 *   pending   — computed from the month's received payments; held.
 *   payable   — the refund window (30 days / first use) has closed; safe to pay.
 *   paid      — disbursed on the pay cycle.
 *   cancelled — a refund was issued before payout; the statement (or the
 *               refunded portion) is voided so nothing is ever clawed back.
 * `payableAfter` is when `pending → payable` may occur (month-end + window).
 */
export const CommissionStatementStateSchema = z.enum([
    'pending',
    'payable',
    'paid',
    'cancelled',
]);
export const CommissionStatementSchema = z.object({
    id: IDSchema,
    type: z.literal('commission_statement'),
    schemaVersion: SchemaVersionSchema,
    recipientRepEmail: EmailSchema,
    /** Central (America/Chicago) calendar month of receipts, `YYYY-MM` (SO-001 §6.3 payment-received basis; RIG-654). */
    periodMonth: z.string().regex(/^\d{4}-\d{2}$/),
    /** Plan version applied (grandfathering / audit). */
    planConfigVersion: z.string(),
    state: CommissionStatementStateSchema.default('pending'),
    /** When `pending → payable` becomes allowed (month-end + refund window). */
    payableAfter: IsoTimestampSchema,
    // ── Computed breakdown (mirrors the engine result) ──────────────────────
    /** §2 tier base input — new-deal gross revenue received this month. */
    newDealRevenueCents: MoneyCentsSchema,
    /** §2 marginal base commission. */
    baseCents: MoneyCentsSchema,
    /** §3 streak active for this month. */
    streakActive: z.boolean().default(false),
    /** §3 streak bonus (already included in earnings). */
    streakBonusCents: MoneyCentsSchema.default(0),
    /** §4 total renewal commission (flat 5% lines). */
    renewalTotalCents: MoneyCentsSchema.default(0),
    /** §5 total ramp-up bonuses. */
    rampUpTotalCents: MoneyCentsSchema.default(0),
    /** base + streak + renewals + ramp-up (before clawback/override). */
    grossEarnedCents: MoneyCentsSchema,
    /** §6.4 clawback (should stay 0 under the arrears model). */
    clawbackCents: MoneyCentsSchema.default(0),
    /** §7 signed manual adjustment (rolling-quarter / anti-gaming / correction). */
    overrideAdjustmentCents: z.number().int().default(0),
    overrideReason: z.string().optional(),
    /** grossEarned − clawback + override. Signed (override can push negative). */
    netPayableCents: z.number().int(),
    /** Full itemized engine breakdown (tier rows, renewal lines, ramp-up) for display. */
    breakdown: z.record(z.string(), z.unknown()).default({}),
    /** Provenance — the paid invoices that fed this statement. */
    sourceInvoiceIds: z.array(IDSchema).default([]),
    finalizedAt: IsoTimestampSchema.optional(),
    paidAt: IsoTimestampSchema.optional(),
    paidBy: z.string().optional(),
    paymentReference: z.string().optional(),
    history: z.array(HistoryEntrySchema).default([]),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deletedAt: IsoTimestampSchema.nullable().optional(),
    version: z.number().int().positive(),
});
//# sourceMappingURL=commission-statement.js.map